import { Form, Formik } from 'formik';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaExclamationCircle } from 'react-icons/all';
import { useDispatch, useSelector } from 'react-redux';
import { UserProfile } from '../../../../@posturinn/client-react';
import { closeAllModals } from '../../../../actions/modalActions';
import { createPaymentCard } from '../../../../actions/paymentActions';
import { getQueryParam } from '../../../../helpers/url/urlHelpers';
import IUserProfileReducer from '../../../../interfaces/reducers/userProfileReducer';
import { IStore } from '../../../../reducers';
import Button from '../../../base/button/button';
import FormikCheckbox from '../../../base/forms/formikCheckbox';
import FormikFieldWrapper from '../../../base/forms/formikFieldWrapper';
import { StyledFormikField } from '../../../base/forms/input';
import { Column, Columns } from '../../../base/grid';
import { H2 } from '../../../base/typography';
import * as StyledModals from '../../modals/myShipmentsModals.styles';
import * as StyledOnboarding from '../onboarding.styles';
import { stepPaymentInitialValues, stepPaymentSchema } from './stepPayment.schema';

interface Props {
	Proceed: any;
	NewUserProfile: UserProfile;
	SetNewUserProfile: any;
}

const StepPayment: FC<Props> = ({ Proceed, NewUserProfile, SetNewUserProfile }) => {
	const { t } = useTranslation('myShipments');
	const dispatch = useDispatch<any>();
	const error = getQueryParam('error');
	const userProfileReducer: IUserProfileReducer = useSelector<IStore, IUserProfileReducer>(
		store => store.userProfileReducer
	);
	const { modalSubmitPending, modalSubmitError } = userProfileReducer;
	const [submitted, setSubmitted] = useState(false);
	if (submitted && !modalSubmitError && !modalSubmitPending) {
		dispatch(closeAllModals());
		Proceed();
	}

	const addPaymentMethod = values => {
		dispatch(
			createPaymentCard(
				values.cardholderName,
				values.cardNumber,
				values.cvc,
				values.expiryDate.substring(0, 2),
				values.expiryDate.substring(values.expiryDate.length - 2)
			)
		);
	};

	const submitStepPayment = values => {
		setSubmitted(false);

		addPaymentMethod(values);

		if (values.automaticPayments) {
			NewUserProfile.paymentCardId = 'SET';
			SetNewUserProfile(NewUserProfile);
		}

		setSubmitted(true);
	};

	return (
		<StyledOnboarding.LeftColumn>
			<Formik
				initialValues={stepPaymentInitialValues}
				enableReinitialize
				validationSchema={stepPaymentSchema}
				onSubmit={submitStepPayment}>
				{({ setFieldValue }) => (
					<Form>
						<H2>{t('onboarding.payment.heading')}</H2>

						<StyledOnboarding.InfoText>{t('onboarding.payment.infoText')}</StyledOnboarding.InfoText>

						{error && (
							<StyledModals.ErrorMessage>
								<FaExclamationCircle />
								<div>{error}</div>
							</StyledModals.ErrorMessage>
						)}

						{modalSubmitError && (
							<StyledModals.ErrorMessage>
								<FaExclamationCircle />
								<div>{t('genericError') as string}</div>
							</StyledModals.ErrorMessage>
						)}

						<Columns>
							<Column>
								<FormikFieldWrapper
									name="cardholderName"
									languageComponent="myShipments"
									label={t('cardholder')}
									smallLabel>
									<StyledFormikField
										type="text"
										id="cardholderName"
										name="cardholderName"
										placeholder={t('cardholderPlaceholder')}
									/>
								</FormikFieldWrapper>
							</Column>
							<Column>
								<FormikFieldWrapper
									name="cardNumber"
									languageComponent="myShipments"
									label={t('cardnumber')}
									smallLabel>
									<StyledFormikField
										type="text"
										id="cardNumber"
										name="cardNumber"
										placeholder={t('cardnumberPlaceholder')}
									/>
								</FormikFieldWrapper>
							</Column>
							<Column desktopSmall={50 / 100}>
								<FormikFieldWrapper
									name="expiryDate"
									languageComponent="myShipments"
									label={t('expiryDate')}
									smallLabel>
									<StyledFormikField
										type="text"
										id="expiryDate"
										name="expiryDate"
										placeholder={t('expiryDatePlaceholder')}
									/>
								</FormikFieldWrapper>
							</Column>
							<Column desktopSmall={50 / 100}>
								<FormikFieldWrapper
									name="cvc"
									languageComponent="myShipments"
									label={t('cvc')}
									smallLabel>
									<StyledFormikField
										type="text"
										id="cvc"
										name="cvc"
										placeholder={t('cvcPlaceholder')}
									/>
								</FormikFieldWrapper>
							</Column>
						</Columns>

						<StyledOnboarding.InfoText checkbox>
							{t('onboarding.payment.automaticPayments')}
						</StyledOnboarding.InfoText>
						<FormikCheckbox
							id="automaticPayments"
							name="automaticPayments"
							label={t('onboarding.payment.automaticPaymentsCheckbox') as string}
							setFieldValue={setFieldValue}
						/>

						<Button primary type="submit" isLoading={modalSubmitPending}>
							{t('onboarding.complete')}
						</Button>

						<StyledOnboarding.SkipLink onClick={Proceed}>{t('onboarding.skip')}</StyledOnboarding.SkipLink>
					</Form>
				)}
			</Formik>
		</StyledOnboarding.LeftColumn>
	);
};
export default StepPayment;
